import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Tab, Tabs, Table, Spinner, Badge } from "react-bootstrap";
import {
  MdUpload,
  MdDownload,
  MdRefresh,
  MdCloudUpload,
  MdBed,
  MdSearch,
  MdPerson
} from "react-icons/md";
import asignacionCamasTrabajadoresApi from "../../../services/api/asignacionCamasTrabajadoresApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../components/css/Dashboard.css";

const AsignacionCamasTrabajadores = () => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Estados para reserva masiva
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [results, setResults] = useState(null);
  const [fileValidationErrors, setFileValidationErrors] = useState([]);
  const [isFileValid, setIsFileValid] = useState(false);
  
  // Estados para reserva individual
  const [activeTab, setActiveTab] = useState("masiva");
  const [busquedaTrabajador, setBusquedaTrabajador] = useState("");
  const [trabajadoresEncontrados, setTrabajadoresEncontrados] = useState([]);
  const [buscandoTrabajadores, setBuscandoTrabajadores] = useState(false);
  const [creandoReserva, setCreandoReserva] = useState(false);
  const [fechaInicioReserva, setFechaInicioReserva] = useState("");
  const [fechaFinReserva, setFechaFinReserva] = useState("");

  const showToast = (type, message) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000 });
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(fileExtension)) {
        showToast("error", 'Por favor seleccione un archivo Excel válido (.xlsx o .xls)');
        e.target.value = '';
        return;
      }

      // Validar archivo en el backend
      setIsValidating(true);
      setFileValidationErrors([]);
      setIsFileValid(false);
      setResults(null);

      try {
        const validationResponse = await asignacionCamasTrabajadoresApi.validateFile(
          selectedFile,
          selectedProject?.id
        );

        if (validationResponse.data) {
          const validationData = validationResponse.data;

          if (validationData.success && validationData.total_errores === 0) {
            setIsFileValid(true);
            setFile(selectedFile);
            showToast("success", `Archivo válido. ${validationData.filas_validas} filas listas para procesar.`);
          } else {
            setIsFileValid(false);
            setFileValidationErrors(validationData.errores || []);
            showToast("warning", `Se encontraron ${validationData.total_errores || 0} errores en el archivo. Revise los detalles.`);
          }
        }
      } catch (error) {
        console.error('Error al validar archivo:', error);
        setIsFileValid(false);
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.errores && Array.isArray(errorData.errores)) {
            setFileValidationErrors(errorData.errores);
          } else {
            showToast("error", errorData.detail || errorData.message || "Error al validar el archivo");
          }
        } else {
          showToast("error", "Error de conexión. Verifica tu conexión a internet.");
        }
      } finally {
        setIsValidating(false);
      }
    }
  };

  const handleProcessUpload = async () => {
    if (!file || !isFileValid) {
      showToast("warning", "Por favor, primero valide un archivo Excel válido.");
      return;
    }

    if (!selectedProject?.id) {
      showToast("error", "Debe seleccionar un proyecto.");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const response = await asignacionCamasTrabajadoresApi.procesarAsignaciones(
        file,
        selectedProject.id
      );

      if (response.data) {
        const resultados = response.data;

        if (resultados.success) {
          setResults({
            asignaciones_exitosas: resultados.asignaciones_exitosas || 0,
            asignaciones_fallidas: resultados.asignaciones_fallidas || 0,
            detalle_exitosas: resultados.detalle_exitosas || [],
            detalle_fallidas: resultados.detalle_fallidas || []
          });

          showToast("success", `Procesamiento completado: ${resultados.asignaciones_exitosas} asignaciones exitosas, ${resultados.asignaciones_fallidas} fallidas.`);
        } else {
          showToast("error", resultados.message || "Error al procesar las asignaciones");
        }
      }
    } catch (error) {
      console.error('Error en procesamiento:', error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        showToast("error", errorData.detail || errorData.message || "Error al procesar las asignaciones");
      } else {
        showToast("error", 'Error de conexión. Verifica tu conexión a internet.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        DNI: "12345678",
        "Fecha Inicio": "2026-07-01",
        "Fecha Fin": "2026-07-22",
      },
      {
        DNI: "87654321",
        "Fecha Inicio": "2026-07-01",
        "Fecha Fin": "2026-07-22",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template_asignacion_camas.xlsx");

    showToast("success", "Template descargado exitosamente");
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    setIsFileValid(false);
    setFileValidationErrors([]);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    showToast("info", "Formulario reiniciado");
  };

  // Funciones para reserva individual
  const handleBuscarTrabajadores = async () => {
    if (!busquedaTrabajador || busquedaTrabajador.trim().length < 2) {
      showToast("warning", "Ingrese al menos 2 caracteres para buscar");
      return;
    }

    if (!selectedProject?.id) {
      showToast("error", "Debe seleccionar un proyecto");
      return;
    }

    setBuscandoTrabajadores(true);
    setTrabajadoresEncontrados([]);

    try {
      const response = await asignacionCamasTrabajadoresApi.buscarTrabajadores(
        busquedaTrabajador.trim(),
        selectedProject.id
      );

      if (response.data && response.data.success) {
        setTrabajadoresEncontrados(response.data.data || []);
        if (response.data.count === 0) {
          showToast("info", "No se encontraron trabajadores con ese criterio de búsqueda");
        }
      } else {
        showToast("error", response.data?.message || "Error al buscar trabajadores");
      }
    } catch (error) {
      console.error("Error al buscar trabajadores:", error);
      showToast("error", error.response?.data?.detail || "Error de conexión");
    } finally {
      setBuscandoTrabajadores(false);
    }
  };

  const handleCrearReserva = async (trabajadorId) => {
    if (!selectedProject?.id) {
      showToast("error", "Debe seleccionar un proyecto");
      return;
    }

    if (!fechaInicioReserva) {
      showToast("error", "La fecha inicio de estadía es obligatoria");
      return;
    }

    if (!fechaFinReserva) {
      showToast("error", "La fecha fin de estadía es obligatoria");
      return;
    }

    if (fechaFinReserva < fechaInicioReserva) {
      showToast("error", "La fecha fin no puede ser anterior a la fecha inicio");
      return;
    }

    setCreandoReserva(true);

    try {
      const response = await asignacionCamasTrabajadoresApi.crearReservaIndividual(
        trabajadorId,
        selectedProject.id,
        {
          fechaInicioEstadia: fechaInicioReserva,
          fechaFinEstadia: fechaFinReserva,
        }
      );

      if (response.data && response.data.success) {
        showToast("success", `Reserva creada exitosamente para ${response.data.trabajador?.nombre_completo || "el trabajador"}`);
        // Limpiar búsqueda y resultados
        setBusquedaTrabajador("");
        setTrabajadoresEncontrados([]);
      } else {
        // Si tiene reserva existente, mostrar información detallada
        if (response.data?.tiene_reserva && response.data?.reserva_existente) {
          const reserva = response.data.reserva_existente;
          const estado = reserva.estado_asignacion === "reservada" ? "Reservada" : "Activa";
          const fechaReserva = reserva.fecha_reserva 
            ? new Date(reserva.fecha_reserva).toLocaleString("es-PE")
            : "N/A";
          
          const mensaje = `El trabajador ya tiene una cama ${estado.toLowerCase()}.\n\n` +
            `📋 Detalles de la reserva:\n` +
            `• Cama: ${reserva.cama?.numero_cama || "N/A"}\n` +
            `• Habitación: ${reserva.cama?.habitacion || "N/A"}\n` +
            `• Módulo: ${reserva.cama?.modulo || "N/A"}\n` +
            `• Zona: ${reserva.cama?.zona || "N/A"}\n` +
            `• Estado: ${estado}\n` +
            `• Fecha de Reserva: ${fechaReserva}`;
          
          alert(mensaje);
        } else {
          showToast("error", response.data?.message || "Error al crear la reserva");
        }
      }
    } catch (error) {
      console.error("Error al crear reserva:", error);
      // Verificar si el error contiene información de reserva existente
      if (error.response?.data?.tiene_reserva && error.response?.data?.reserva_existente) {
        const reserva = error.response.data.reserva_existente;
        const estado = reserva.estado_asignacion === "reservada" ? "Reservada" : "Activa";
        const fechaReserva = reserva.fecha_reserva 
          ? new Date(reserva.fecha_reserva).toLocaleString("es-PE")
          : "N/A";
        
        const mensaje = `El trabajador ya tiene una cama ${estado.toLowerCase()}.\n\n` +
          `📋 Detalles de la reserva:\n` +
          `• Cama: ${reserva.cama?.numero_cama || "N/A"}\n` +
          `• Habitación: ${reserva.cama?.habitacion || "N/A"}\n` +
          `• Módulo: ${reserva.cama?.modulo || "N/A"}\n` +
          `• Zona: ${reserva.cama?.zona || "N/A"}\n` +
          `• Estado: ${estado}\n` +
          `• Fecha de Reserva: ${fechaReserva}`;
        
        alert(mensaje);
      } else {
        showToast("error", error.response?.data?.detail || error.response?.data?.message || "Error de conexión");
      }
    } finally {
      setCreandoReserva(false);
    }
  };

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          .dh-carga-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          .dh-card-main {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
          }
          
          .dh-header-title {
            color: white;
            font-weight: 700;
            font-size: 1.5rem;
            margin: 0;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-info:hover {
            background: linear-gradient(135deg, #0072ff 0%, #0056cc 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 0.95rem;
            transition: all 0.3s ease;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            outline: none;
          }
          
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          .dh-alert-success {
            background: linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%);
            border-left: 4px solid #11998e;
            color: #047857;
          }
          
          .dh-alert-danger {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
            border-left: 4px solid #ef4444;
            color: #991b1b;
          }
          
          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
            border-left: 4px solid #f59e0b;
            color: #92400e;
          }
        `}
      </style>

      <div className="dh-carga-bg">
        <ToastContainer />
        <div className="container-fluid py-4">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="card dh-card-main">
                <div className="card-header dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <MdBed className="me-3" size={28} />
                      <h3 className="dh-header-title mb-0">
                        Reserva Automática de Camas
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {/* Proyecto */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Proyecto</label>
                    <input
                      type="text"
                      className="form-control dh-form-control"
                      value={selectedProject?.nombre || "No seleccionado"}
                      disabled
                    />
                  </div>

                  {/* Pestañas */}
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4"
                  >
                    {/* Pestaña: Reserva Masiva */}
                    <Tab eventKey="masiva" title={
                      <span>
                        <MdCloudUpload className="me-2" />
                        Reserva Masiva
                      </span>
                    }>
                      <div className="mt-4">
                        {/* Información */}
                        <div className="alert alert-info dh-alert mb-4">
                          <strong>Instrucciones:</strong>
                          <ul className="mb-0 mt-2">
                            <li>El archivo Excel debe contener las columnas <strong>"DNI"</strong>, <strong>"Fecha Inicio"</strong> y <strong>"Fecha Fin"</strong> (obligatorias).</li>
                            <li>El sistema reservará automáticamente una cama disponible para cada trabajador.</li>
                            <li>Una vez reservada, la cama cambiará su estado a <strong>"reservado"</strong>.</li>
                            <li>Cada trabajador solo puede tener una reserva de cama activa a la vez.</li>
                          </ul>
                        </div>

                  {/* Descargar Template */}
                  <div className="mb-4">
                    <button
                      className="btn btn-dh-info"
                      onClick={downloadTemplate}
                      disabled={isLoading}
                    >
                      <MdDownload className="me-2" />
                      Descargar Template
                    </button>
                  </div>

                  {/* Cargar Archivo */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Seleccionar Archivo Excel</label>
                    <input
                      type="file"
                      className="form-control dh-form-control"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      disabled={isLoading || isValidating}
                    />
                    {isValidating && (
                      <div className="mt-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Validando...</span>
                        </div>
                        <span>Validando archivo...</span>
                      </div>
                    )}
                  </div>

                  {/* Errores de Validación */}
                  {fileValidationErrors && Array.isArray(fileValidationErrors) && fileValidationErrors.length > 0 && (
                    <div className="alert alert-danger dh-alert dh-alert-danger mb-4">
                      <strong>Errores encontrados ({fileValidationErrors.length}):</strong>
                      <div className="mt-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {fileValidationErrors.map((error, index) => (
                          <div key={index} className="mb-2">
                            <strong>Fila {error.fila}:</strong>
                            <ul className="mb-0">
                              {error.errores && Array.isArray(error.errores) && error.errores.map((err, errIndex) => (
                                <li key={errIndex}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Resultados */}
                  {results && (
                    <div className="alert alert-success dh-alert dh-alert-success mb-4">
                      <h5>Resultados del Procesamiento</h5>
                      <p className="mb-2">
                        <strong>Asignaciones Exitosas:</strong> {results.asignaciones_exitosas}
                      </p>
                      <p className="mb-2">
                        <strong>Asignaciones Fallidas:</strong> {results.asignaciones_fallidas}
                      </p>
                      {results.detalle_exitosas && results.detalle_exitosas.length > 0 && (
                        <div className="mt-3">
                          <strong>Detalle de Asignaciones Exitosas:</strong>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }} className="mt-2">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Fila</th>
                                  <th>DNI</th>
                                  <th>Trabajador</th>
                                  <th>Cama</th>
                                  <th>Habitación</th>
                                  <th>Módulo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {results.detalle_exitosas && Array.isArray(results.detalle_exitosas) && results.detalle_exitosas.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.fila}</td>
                                    <td>{item.dni}</td>
                                    <td>{item.trabajador}</td>
                                    <td>{item.numero_cama}</td>
                                    <td>{item.habitacion}</td>
                                    <td>{item.modulo}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {results.detalle_fallidas && results.detalle_fallidas.length > 0 && (
                        <div className="mt-3">
                          <strong>Detalle de Asignaciones Fallidas:</strong>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }} className="mt-2">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Fila</th>
                                  <th>DNI</th>
                                  <th>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {results.detalle_fallidas && Array.isArray(results.detalle_fallidas) && results.detalle_fallidas.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.fila}</td>
                                    <td>{item.dni}</td>
                                    <td>{item.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                        {/* Botones de Acción */}
                        <div className="d-flex gap-3 justify-content-end">
                          <button
                            className="btn btn-dh-secondary"
                            onClick={resetForm}
                            disabled={isLoading}
                          >
                            <MdRefresh className="me-2" />
                            Reiniciar
                          </button>
                          <button
                            className="btn btn-dh-primary"
                            onClick={handleProcessUpload}
                            disabled={!isFileValid || isLoading || !file}
                          >
                            {isLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Procesando...
                              </>
                            ) : (
                              <>
                                <MdCloudUpload className="me-2" />
                                Procesar Asignaciones
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </Tab>

                    {/* Pestaña: Reserva Individual */}
                    <Tab eventKey="individual" title={
                      <span>
                        <MdPerson className="me-2" />
                        Reserva Individual
                      </span>
                    }>
                      <div className="mt-4">
                        {/* Información */}
                        <div className="alert alert-info dh-alert mb-4">
                          <strong>Instrucciones:</strong>
                          <ul className="mb-0 mt-2">
                            <li>Busque un trabajador por su número de documento o por nombre/apellidos.</li>
                            <li>Seleccione el trabajador de la lista, indique las fechas de estadía y haga clic en "Reservar Cama".</li>
                            <li>El sistema asignará automáticamente una cama disponible según las restricciones de género y régimen.</li>
                          </ul>
                        </div>

                        <div className="row mb-4">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              Fecha inicio estadía <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control dh-form-control"
                              value={fechaInicioReserva}
                              onChange={(e) => setFechaInicioReserva(e.target.value)}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              Fecha fin estadía <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control dh-form-control"
                              value={fechaFinReserva}
                              min={fechaInicioReserva || undefined}
                              onChange={(e) => setFechaFinReserva(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        {/* Búsqueda */}
                        <div className="mb-4">
                          <label className="form-label fw-bold">Buscar Trabajador</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control dh-form-control"
                              placeholder="Ingrese DNI o nombre/apellidos del trabajador..."
                              value={busquedaTrabajador}
                              onChange={(e) => setBusquedaTrabajador(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleBuscarTrabajadores();
                                }
                              }}
                              disabled={buscandoTrabajadores}
                            />
                            <button
                              className="btn btn-dh-primary"
                              onClick={handleBuscarTrabajadores}
                              disabled={buscandoTrabajadores || !busquedaTrabajador.trim()}
                            >
                              {buscandoTrabajadores ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                <>
                                  <MdSearch className="me-2" />
                                  Buscar
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Grilla de Trabajadores */}
                        {trabajadoresEncontrados.length > 0 && (
                          <div className="mb-4">
                            <h5 className="mb-3">Trabajadores Encontrados ({trabajadoresEncontrados.length})</h5>
                            <div className="table-responsive">
                              <Table striped bordered hover>
                                <thead className="table-light">
                                  <tr>
                                    <th>Documento</th>
                                    <th>Nombre Completo</th>
                                    <th>Cargo</th>
                                    <th>Área</th>
                                    <th>Género</th>
                                    <th>Régimen</th>
                                    <th>Estado Reserva</th>
                                    <th>Acción</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {trabajadoresEncontrados.map((trabajador) => {
                                    const tieneReserva = trabajador.tiene_reserva || false;
                                    const reservaInfo = trabajador.reserva_info;
                                    const estadoReserva = reservaInfo?.estado_asignacion || null;
                                    
                                    return (
                                      <tr key={trabajador.id}>
                                        <td>{trabajador.numero_documento_identidad}</td>
                                        <td className="fw-bold">{trabajador.nombre_completo}</td>
                                        <td>{trabajador.cargo || "-"}</td>
                                        <td>{trabajador.area || "-"}</td>
                                        <td>
                                          <Badge bg="info">{trabajador.genero || "-"}</Badge>
                                        </td>
                                        <td>
                                          <Badge bg="secondary">
                                            {trabajador.regimen_codigo || trabajador.regimen_nombre || "-"}
                                          </Badge>
                                        </td>
                                        <td>
                                          {tieneReserva && reservaInfo ? (
                                            <div>
                                              <Badge bg={estadoReserva === "activa" ? "success" : "warning"} className="mb-1">
                                                {estadoReserva === "activa" ? "Activa" : "Reservada"}
                                              </Badge>
                                              <div className="small text-muted">
                                                <div>Cama: {reservaInfo.cama?.numero_cama || "N/A"}</div>
                                                <div>Hab: {reservaInfo.cama?.habitacion || "N/A"}</div>
                                                <div>Módulo: {reservaInfo.cama?.modulo || "N/A"}</div>
                                              </div>
                                            </div>
                                          ) : (
                                            <Badge bg="secondary">Sin reserva</Badge>
                                          )}
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-sm btn-dh-primary"
                                            onClick={() => handleCrearReserva(trabajador.id)}
                                            disabled={
                                              creandoReserva ||
                                              tieneReserva ||
                                              !fechaInicioReserva ||
                                              !fechaFinReserva
                                            }
                                            title={
                                              tieneReserva
                                                ? "El trabajador ya tiene una reserva activa"
                                                : !fechaInicioReserva || !fechaFinReserva
                                                  ? "Indique fecha inicio y fin de estadía"
                                                  : ""
                                            }
                                          >
                                            {creandoReserva ? (
                                              <Spinner size="sm" animation="border" />
                                            ) : (
                                              <>
                                                <MdBed className="me-1" />
                                                {tieneReserva ? "Ya tiene reserva" : "Reservar Cama"}
                                              </>
                                            )}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AsignacionCamasTrabajadores;
